// ---------------------------------------------------------------------------
// Zentrales i18n — sieben Sprachen: en (Referenz + Fallback), el, de, ru, ar,
// he, zh. (Stand 11.08.2026 — zh war seit Juli registriert, stand aber weder
// hier noch in market.js/format.js in der Aufzählung.)
//
// Regeln:
//  - Feste UI-Texte kommen NUR aus diesen Dateien (keine Strings in Komponenten).
//  - Neue Keys zuerst in en.js anlegen, dann el/de/ru/ar/he/zh nachziehen.
//  - Fehlt ein Key in einer Sprache, greift Englisch; im Dev-Modus wird das
//    einmalig pro Key in der Konsole protokolliert. In Produktion erscheint
//    niemals ein roher Key — schlimmstenfalls der englische Text.
//  - ar und he sind RTL-Sprachen (siehe config/market.js → rtlLangs); die
//    Textrichtung setzt der LanguageProvider auf <html dir="…">.
// ---------------------------------------------------------------------------
import en from "./en";
import el from "./el";
import de from "./de";
import ru from "./ru";
import ar from "./ar";
import he from "./he";
import zh from "./zh";

export const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "el", label: "EL" },
  { code: "de", label: "DE" },
  { code: "ru", label: "RU" },
  { code: "ar", label: "AR" },
  { code: "he", label: "HE" },
  { code: "zh", label: "中文" },
];

export const SUPPORTED_LANGS = ["en", "el", "de", "ru", "ar", "he", "zh"];
export const DEFAULT_LANG = "en";
export const RTL_LANGS = ["ar", "he"];

export const translations = { en, el, de, ru, ar, he, zh };

export function isSupportedLang(code) {
  return typeof code === "string" && SUPPORTED_LANGS.includes(code);
}

export function isRtlLang(code) {
  return RTL_LANGS.includes(code);
}

function lookup(obj, parts) {
  for (const p of parts) obj = obj?.[p];
  return obj;
}

// Einmal pro (Sprache, Key) warnen, damit die Konsole lesbar bleibt.
const warnedKeys = new Set();

// t(lang, "auth.welcome_name", { name: "Ana" }) — {platzhalter} werden ersetzt.
export function t(lang, path, vars) {
  const parts = path.split(".");
  let value = lookup(translations[lang] || translations[DEFAULT_LANG], parts);
  if (value == null) {
    if (process.env.NODE_ENV === "development") {
      const warnId = `${lang}:${path}`;
      if (!warnedKeys.has(warnId)) {
        warnedKeys.add(warnId);
        // eslint-disable-next-line no-console
        console.warn(`[i18n] Fehlender Key "${path}" für Sprache "${lang}" — Fallback auf "${DEFAULT_LANG}".`);
      }
    }
    value = lookup(translations[DEFAULT_LANG], parts);
  }
  if (value == null) return path;
  if (vars && typeof value === "string") {
    for (const [k, v] of Object.entries(vars)) {
      value = value.split(`{${k}}`).join(String(v));
    }
  }
  return value;
}

// Die Browsersprach-Erkennung ist am 11.08.2026 entfallen. Sie war Stufe 4 der
// Auflösung und entschied damit, was auf der unpräfixierten URL steht — obwohl
// die zugleich die englische und die x-default-Fassung ist. Englisch ist die
// Systemsprache der Plattform; Abweichungen kommen aus dem Pfad-Präfix, aus
// localStorage oder aus der Kontosprache. Falls die Erkennung je zurückkommt:
// Sie stand zwischen `?lang=` und DEFAULT_LANG in `resolveInitialLang`.

// Sprach-Präfix aus dem URL-Pfad: /de/... → "de", /el → "el", sonst null.
// Englisch hat BEWUSST kein Präfix — die unpräfixierte URL ist zugleich die
// englische Fassung und das hreflang-x-default (ein URL-Paar weniger, das
// dupliziert werden könnte; Standardmuster, das Google ausdrücklich erlaubt).
// Deshalb taucht "en" hier nicht auf.
const PREFIX_RE = /^\/(el|de|ru|ar|he|zh)(\/|$)/;

export function urlLangPrefix(pathname) {
  try {
    const p = pathname ?? window.location.pathname;
    const m = typeof p === "string" ? p.match(PREFIX_RE) : null;
    return m ? m[1] : null;
  } catch {
    return null; /* kein window (Tests) */
  }
}

// Pfad ohne Sprach-Präfix — "/de/suche" → "/suche", "/de" → "/".
export function stripLangPrefix(pathname) {
  const prefix = urlLangPrefix(pathname);
  if (!prefix) return pathname;
  const rest = pathname.slice(prefix.length + 1);
  return rest === "" ? "/" : rest;
}

// Initialsprache VOR dem Login bestimmen (die Profilsprache des eingeloggten
// Nutzers wendet der LanguageProvider an, sobald /auth/me da ist — aber NICHT,
// wenn ein Pfad-Präfix die Sprache vorgibt, siehe dort):
//   1. Sprach-Präfix im Pfad (/de, /el, …) — die URL ist die stärkste Aussage:
//      Wer einen /de-Link öffnet, hat eine deutsche Seite geteilt bekommen.
//      Stünde localStorage darüber, zeigte dieselbe URL je nach Vorgeschichte
//      verschiedene Sprachen — genau der Audit-Befund 2.2.
//   2. zuvor gespeicherte Sprache im Browser (localStorage)
//   3. Sprache aus der URL (?lang=xx — Altlast, bleibt für bestehende Links)
//   4. Standardsprache der Plattform (en)
//
// Stufe 4 war bis zum 11.08.2026 die BROWSERSPRACHE, `en` griff nur, wenn sie
// keine der sieben war. Damit entschied der Browser des Besuchers, was auf der
// unpräfixierten URL steht — und die ist zugleich die englische und die
// x-default-Fassung. Ein deutscher Browser bekam dort Deutsch, während
// Canonical und hreflang Englisch behaupteten und der prerenderte englische
// Schnappschuss nach dem JS-Start sichtbar umkippte. Englisch ist die
// Systemsprache; wer eine andere will, klickt die Flagge (bleibt in
// localStorage) oder öffnet einen /de-, /el-, …-Link.
export function resolveInitialLang() {
  const fromPath = urlLangPrefix();
  if (fromPath) return fromPath;
  try {
    const stored = localStorage.getItem("lang");
    if (isSupportedLang(stored)) return stored;
  } catch {
    /* localStorage kann in Private-Mode fehlen */
  }
  try {
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (isSupportedLang(urlLang)) return urlLang;
  } catch {
    /* kein window (Tests) */
  }
  return DEFAULT_LANG;
}
